'use client'

/**
 * useAggregateCashFlowCurves
 *
 * Shared data-fetching + heavy memoized derivations used by both
 * CompactCashFlowView and ExpandedCashFlowView. Centralises the ~100 lines
 * of preRangeObligation / outlayRatio / ceiling-alignment logic so the two
 * views always compute identical curves.
 *
 * Uses `pointToDate` consistently throughout, fixing the subtle
 * `new Date(p.year, p.month - 1, 1)` vs `pointToDate(p)` inconsistency that
 * existed between the two views.
 */
import { useMemo } from 'react'

import {
  OUTLAY_REPORTING_START,
  computeAggregateOutlay,
  computeCumulativeObligation,
  computeRealOutlayCurve,
  filterIdvParents,
  pointToDate,
  safeNum,
} from '@/components/usaspending/charts/cashflow-utils'

import type { AwardPortfolioItem, TimeSeriesPoint } from '@/types/usaspending'

import {
  useRecipientAwardPortfolio,
  useRecipientCeilingTimeSeries,
  useRecipientOutlayTimeSeries,
  useSuspenseRecipientTimeSeries,
} from './useRecipientAnalytics'

export function useAggregateCashFlowCurves(recipientId: string) {
  const { data: timeSeriesData } = useSuspenseRecipientTimeSeries(recipientId)

  const { data: portfolioData, isFetching: isPortfolioFetching } =
    useRecipientAwardPortfolio(recipientId)
  const { data: outlayTimeSeriesData, isFetching: isOutlayFetching } =
    useRecipientOutlayTimeSeries(recipientId)
  const { data: ceilingTimeSeriesData, isFetching: isCeilingFetching } =
    useRecipientCeilingTimeSeries(recipientId)

  const timeSeries = timeSeriesData as TimeSeriesPoint[]
  const allAwards = portfolioData?.results as AwardPortfolioItem[] | undefined
  const outlayTimeSeries = outlayTimeSeriesData as TimeSeriesPoint[] | undefined
  const ceilingTimeSeries = ceilingTimeSeriesData as
    | TimeSeriesPoint[]
    | undefined

  // Filter out IDV parent records to prevent vehicle-level double-counting.
  // Child task orders carry the actual per-recipient obligation/ceiling values;
  // including IDV parents inflates aggregates and creates chart-distorting spikes.
  const { awards, excludedIdvTypes } = useMemo(() => {
    if (!allAwards)
      return { awards: undefined, excludedIdvTypes: [] as string[] }
    const result = filterIdvParents(allAwards)
    return { awards: result.awards, excludedIdvTypes: result.excludedTypes }
  }, [allAwards])

  // Pre-range obligation: total portfolio obligation minus what the time series covers.
  // Seeds the cumulative curve so it doesn't start at 0 when awards predate the series.
  const preRangeObligation = useMemo(() => {
    if (!portfolioData) return 0
    const totalPortfolioObl = safeNum(portfolioData.total_obligation)
    const totalTimeSeriesObl = timeSeries.reduce((sum, p) => sum + p.amount, 0)
    return Math.max(0, totalPortfolioObl - totalTimeSeriesObl)
  }, [portfolioData, timeSeries])

  const obligationCurve = useMemo(
    () => computeCumulativeObligation(timeSeries, preRangeObligation),
    [timeSeries, preRangeObligation],
  )

  // Ceiling from server-side time series (covers ALL awards, no cap).
  // Align visible curve with obligation date range: ceiling before the obligation
  // start date becomes the preRange seed so both curves start at the same X position.
  const { ceilingCurve, ceilingPreRange } = useMemo(() => {
    if (
      !ceilingTimeSeries ||
      ceilingTimeSeries.length === 0 ||
      timeSeries.length === 0
    )
      return { ceilingCurve: null, ceilingPreRange: 0 }

    // Find actual earliest month (TS may be unsorted)
    let earliest = timeSeries[0]
    for (const p of timeSeries) {
      if (
        p.year < earliest.year ||
        (p.year === earliest.year && p.month < earliest.month)
      )
        earliest = p
    }
    const oblStart = pointToDate(earliest)

    let preRange = 0
    const visiblePoints: TimeSeriesPoint[] = []
    for (const p of ceilingTimeSeries) {
      const d = pointToDate(p)
      if (d < oblStart) {
        preRange += p.amount
      } else {
        visiblePoints.push(p)
      }
    }

    if (portfolioData) {
      const totalCeiling = safeNum(portfolioData.total_ceiling)
      const tsCeiling = ceilingTimeSeries.reduce((sum, p) => sum + p.amount, 0)
      preRange += Math.max(0, totalCeiling - tsCeiling)
    }

    if (visiblePoints.length === 0)
      return { ceilingCurve: null, ceilingPreRange: preRange }

    return {
      ceilingCurve: computeCumulativeObligation(visiblePoints, preRange),
      ceilingPreRange: preRange,
    }
  }, [ceilingTimeSeries, timeSeries, portfolioData])

  // Aggregate outlay ratio used for pre-DATA estimation fallback.
  const outlayRatio = useMemo(() => {
    if (!portfolioData) return 0
    const totalObl = safeNum(portfolioData.total_obligation)
    const totalOut = safeNum(portfolioData.total_outlays)
    const portfolioRatio = totalObl > 0 ? totalOut / totalObl : 0

    let reportingRatio = 0
    if (
      outlayTimeSeries &&
      outlayTimeSeries.length > 0 &&
      timeSeries.length > 0
    ) {
      let reportingObl = 0
      let reportingOut = 0

      for (const p of timeSeries) {
        const d = pointToDate(p)
        if (d >= OUTLAY_REPORTING_START) reportingObl += p.amount
      }
      for (const p of outlayTimeSeries) {
        const d = pointToDate(p)
        if (d >= OUTLAY_REPORTING_START) reportingOut += p.amount
      }

      reportingRatio = reportingObl > 0 ? reportingOut / reportingObl : 0
    }

    const resolved = Math.max(portfolioRatio, reportingRatio)
    return Number.isFinite(resolved) ? Math.max(0, Math.min(1, resolved)) : 0
  }, [portfolioData, outlayTimeSeries, timeSeries])

  // Portfolio pre-reporting seed used to anchor the hybrid real outlay curve.
  // Derived from total outlays minus File C outlays in the mandatory reporting era.
  const preReportingOutlay = useMemo(() => {
    if (!portfolioData) return null
    const totalOutlay = safeNum(portfolioData.total_outlays)
    if (!outlayTimeSeries || outlayTimeSeries.length === 0) return totalOutlay

    let reportingOutlay = 0
    for (const p of outlayTimeSeries) {
      const d = pointToDate(p)
      if (d >= OUTLAY_REPORTING_START) reportingOutlay += p.amount
    }

    const preReporting = totalOutlay - reportingOutlay
    return Number.isFinite(preReporting) ? Math.max(0, preReporting) : null
  }, [portfolioData, outlayTimeSeries])

  const outlayCurve = useMemo(() => {
    // Prefer real File C outlay data when available — hybrid curve:
    // estimated proportional for pre-reporting, real for post-reporting.
    // Wait for portfolioData before computing: outlayRatio and preReportingOutlay
    // depend on it, and without it the curve would use ratio=0 / seed=null,
    // producing incorrect values until portfolio data arrives.
    if (outlayTimeSeries && outlayTimeSeries.length > 0) {
      if (!portfolioData) return null
      return computeRealOutlayCurve(
        outlayTimeSeries,
        obligationCurve,
        outlayRatio,
        preReportingOutlay,
      )
    }
    // Fallback to estimated outlay while loading or if no data
    if (!awards) return null
    return computeAggregateOutlay(awards, obligationCurve)
  }, [
    outlayTimeSeries,
    awards,
    obligationCurve,
    outlayRatio,
    preReportingOutlay,
    portfolioData,
  ])

  // DATA Act pre-reporting check — only when real outlay data exists
  const hasRealOutlay = outlayTimeSeries != null && outlayTimeSeries.length > 0
  const hasPreReportingData =
    hasRealOutlay &&
    obligationCurve.length > 0 &&
    obligationCurve[0].x < OUTLAY_REPORTING_START

  // Fraction of total outlay that is estimated (pre-reporting).
  // Used by the chart to highlight the estimated region when significant.
  const estimatedOutlayPct = useMemo(() => {
    if (!portfolioData || preReportingOutlay == null) return 0
    const total = safeNum(portfolioData.total_outlays)
    return total > 0 ? preReportingOutlay / total : 0
  }, [portfolioData, preReportingOutlay])

  const isFetching =
    isPortfolioFetching || isOutlayFetching || isCeilingFetching

  return {
    timeSeries,
    allAwards,
    awards,
    excludedIdvTypes,
    portfolioData,
    outlayTimeSeries,
    ceilingTimeSeries,
    preRangeObligation,
    obligationCurve,
    ceilingCurve,
    ceilingPreRange,
    outlayRatio,
    preReportingOutlay,
    outlayCurve,
    hasRealOutlay,
    hasPreReportingData,
    estimatedOutlayPct,
    isFetching,
  }
}
